import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * Shrink an over-tall/wide live page so it fits its container without scrolling.
 *
 * Post responsive-overhaul, live pages lay out fluidly and the live stage clips
 * overflow (it never scrolls). A few intrinsically dense pages (Number, Weather,
 * Feelings) can still exceed the safe box on shorter/Retina displays. This hook
 * measures the content's NATURAL size (offsetWidth/Height ignore the applied
 * transform, so measuring while scaled is stable) against the container and
 * returns a scale in (0, 1] — it only ever scales DOWN, never up, so on roomy
 * displays the page renders at its natural size.
 *
 * Pair the returned scale with `transform: scale(scale)` on the content
 * (transform-origin: center) inside a flex-centered container.
 *
 * Robustness: the measure runs after every commit (cheap, and the >0.005 guard
 * prevents a setState loop) so it catches the content mounting a tick after the
 * container, late image loads, etc. A ResizeObserver (attached once both refs
 * exist) then handles viewport resizes.
 */
export function useFitScale(
  containerRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
): number {
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);
  const observerRef = useRef<ResizeObserver | null>(null);
  const observingRef = useRef(false);

  // No dependency array: re-measures after every render. Reading
  // offsetWidth/Height (layout, pre-transform) is cheap, and setScale only
  // fires when the value actually moves, so this can't loop.
  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const content = contentRef.current;
      if (!container || !content) return;
      const natW = content.offsetWidth;
      const natH = content.offsetHeight;
      if (!natW || !natH) return;
      const next =
        Math.round(
          Math.min(1, container.clientWidth / natW, container.clientHeight / natH) * 1000,
        ) / 1000;
      if (Math.abs(scaleRef.current - next) > 0.005) {
        scaleRef.current = next;
        setScale(next);
      }
    };

    measure();

    // Attach the ResizeObserver once both elements are mounted (handles the
    // container and content mounting on different commits).
    if (!observingRef.current && containerRef.current && contentRef.current) {
      const ro = new ResizeObserver(measure);
      ro.observe(containerRef.current);
      ro.observe(contentRef.current);
      observerRef.current = ro;
      observingRef.current = true;
    }
  });

  useLayoutEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      observingRef.current = false;
    };
  }, []);

  return scale;
}
